import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from '../../../hooks/useTranslation';
import {
  DEFAULT_RESOURCE_ACCESS,
  ResourceAccess,
} from '../../../interfaces/ResourceAccess';
import { TranslationKey } from '../../../types/i18n';
import {
  normalizeResourceAccess,
  toAccessPayload,
} from '../../../utils/resourceAccess';
import Button from '../Button/Button';
import ResourceAccessFields from '../ResourceAccessFields/ResourceAccessFields';
import Text from '../Text/Text';

interface ShareFormData {
  access: ResourceAccess;
}

interface ShareResourceDialogProps {
  open: boolean;
  onClose: () => void;
  resourceName: string;
  resourceLabelKey: TranslationKey;
  ownerUserId?: string;
  initialAccess?: Partial<ResourceAccess> | null;
  onSave: (access: ResourceAccess) => Promise<void>;
}

const ShareResourceDialog: React.FC<ShareResourceDialogProps> = ({
  open,
  onClose,
  resourceName,
  resourceLabelKey,
  ownerUserId,
  initialAccess,
  onSave,
}) => {
  const { t } = useTranslation();
  const methods = useForm<ShareFormData>({
    defaultValues: {
      access: { ...DEFAULT_RESOURCE_ACCESS },
    },
  });

  useEffect(() => {
    if (open) {
      methods.reset({
        access: normalizeResourceAccess(initialAccess),
      });
    }
  }, [open, initialAccess, methods]);

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = methods.handleSubmit(async data => {
    await onSave(toAccessPayload(data.access));
    handleClose();
  });

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('Share access for {{name}}', { name: resourceName })}
      </DialogTitle>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Stack spacing={2}>
              <Text type="body2">
                {t(
                  'Control who in your company can view and use this {{resource}}.',
                  { resource: t(resourceLabelKey) }
                )}
              </Text>
              <ResourceAccessFields
                ownerUserId={ownerUserId}
                resourceLabelKey={resourceLabelKey}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="outlined" color="inherit" onClick={handleClose}>
              {t('Cancel')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={methods.formState.isSubmitting}
            >
              {t('Save')}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default ShareResourceDialog;
